import moment from 'moment';
import head from 'lodash/head';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import { call, takeLatest, all, select, put } from 'redux-saga/effects';

import {
  PROCEED_TO_TREK_PAYMENT,
  SEARCH_FLIGHTS_IN_SELF_GUIDED_TREKS,
} from '../actions/types';
import {
  storeFlights,
  setLoadingState,
  storeNoFlights,
} from '../actions/trekPaymentReviewActions';

import { history } from '../store';

import api from '../../lib/api';
import makeUrl from '../../lib/makeUrl';
import endpoints from '../../lib/endpoints';
import config from '../../config';

export default function* watchContinueToBuyTrekTemplate() {
  yield all([
    takeLatest(PROCEED_TO_TREK_PAYMENT, buyTrek),
    takeLatest(SEARCH_FLIGHTS_IN_SELF_GUIDED_TREKS, fetchFlights),
  ]);
}

const getTrekPaymentState = state => state.trekPayment;
const getSelectedTrek = state => state.selectedTrek.trek;

function* buyTrek() {
  yield put(setLoadingState(true));
  const { flights } = yield select(getTrekPaymentState);

  const { name, region, id: trekId } = yield select(getSelectedTrek);
  const {
    lodgings,
    paymentType,
    inbound,
    outbound,
    sessionId,
    selectedGuideType,
    noOfPorter,
    noOfPeople: pax,
    trekTemplateStartDate: trekStartDate,
  } = yield select(getTrekPaymentState);

  const trekHasFlights = !isEmpty(flights);

  let reqObjForInvoice = {
    ...getRequestObjForLodgings({
      pax,
      trekId,
      lodgings,
      trekStartDate,
      paymentPlan: paymentType,
    }),
    ...(trekHasFlights
      ? {
          flights: [
            {
              flight_id: outbound.flight_id,
              ...(flights.length === 1 ? {} : { return_flight_id: inbound.flight_id }),
            },
          ],
        }
      : {}),
    ...(noOfPorter > 0 ? { porters: { number: noOfPorter } } : {}),
    ...(selectedGuideType !== 'none' ? { guides: { guide_type: selectedGuideType } } : {}),
  };

  try {
    const {
      data: {
        invoices: [invoiceId],
      },
    } = yield api.postWithoutToken(endpoints.invoice.create, reqObjForInvoice);

    yield put(setLoadingState(false));
    const trek = { id: trekId, name, region };
    const url = trekHasFlights
      ? `${makeUrl.customerDetails(trek, invoiceId)}?session_id=${sessionId}`
      : `${window.location.pathname
          .split('/')
          .slice(0, 3)
          .join('/')}/payment/${invoiceId}`;

    yield call(history.push, url);
  } catch (err) {
    yield put(setLoadingState(false));
    yield put(storeNoFlights());
  }
}

const getRequestObjForLodgings = ({
  pax,
  trekId,
  lodgings,
  paymentPlan,
  trekStartDate,
}) => {
  const logdesReducer = (lodges, lodge, idx) => {
    const { id, roomType, defaultMealPlan } = lodge;
    let checkinDate, checkoutDate;

    function normalDates() {
      checkinDate = moment(trekStartDate)
        .add(Number(lodge.day) - 1, 'day')
        .format('YYYY-MM-DD');
      checkoutDate = moment(checkinDate)
        .add(1, 'day')
        .format('YYYY-MM-DD');
    }

    if (idx > 0) {
      const lastLodge = lodges[lodges.length - 1];
      if (lodge.id === lastLodge.id) {
        checkinDate = lastLodge.checkin_date;
        checkoutDate = moment(trekStartDate)
          .add(Number(lodge.day), 'day')
          .format('YYYY-MM-DD');

        lodges.pop();
      } else normalDates();
    } else normalDates();

    lodges.push({
      id,
      pax,
      room_type: roomType.toLowerCase(),
      payment_plan: paymentPlan,
      checkin_date: checkinDate,
      checkout_date: checkoutDate,
      meal_plan: defaultMealPlan,
    });
    return lodges;
  };

  const lodges = lodgings.reduce(logdesReducer, []);
  const trek = {
    pax,
    id: trekId,
    start_date: trekStartDate,
    payment_plan: paymentPlan,
  };
  return { lodges, trek };
};

// ----------------------------------------
// Flights
// ----------------------------------------

/**
 * outbound means reaching to a preferred destination
 * inbound means arriving from the preferred destination
 * in our typical treks, the KTM-LUA is **outbound** and LUA-KTM is **inbound**
 *  While in Salleri Trek, the LUA-KTM at the end of the trek is **outbound**
 */

function* fetchFlights({ onSuccess, onFailure }) {
  try {
    const userDataForFlights = yield dataForRoundTripOrOneWay();
    const {
      data: { flights, session_id },
    } = yield call(api.postWithoutToken, 'flights/search', userDataForFlights);

    const {
      outbound,
      inbound,
      tripType,
      isFlightsAvailable,
    } = yield sortAndReturnEarliestFlightsWithTripType(flights.outbound, flights.inbound);

    yield put(storeFlights(outbound, inbound, tripType, isFlightsAvailable, session_id));
    if (onSuccess) onSuccess();
  } catch (err) {
    yield put(storeNoFlights());
    if (onFailure) onFailure();
  }
}

function* sortAndReturnEarliestFlightsWithTripType(outbound, inbound) {
  const createSort = arr => key => sortBy(arr, [key]),
    byDepartureTime = 'departure_time',
    sortOutboundFlights = createSort(outbound),
    sortInboundFlights = createSort(inbound);

  let earliestInboundFlight = {},
    earliestOutboundFlight = {},
    tripType = '',
    isFlightsAvailable = false;

  const { flights } = yield select(getTrekPaymentState);
  const isOneWayFlight = flights.length === 1;
  if (isOneWayFlight) {
    tripType = 'O';
    if (!isEmpty(outbound)) {
      isFlightsAvailable = true;
      earliestOutboundFlight = head(sortOutboundFlights(byDepartureTime));
    }
  } else {
    tripType = 'R';
    if (!isEmpty(inbound) && !isEmpty(outbound)) {
      isFlightsAvailable = true;
      earliestOutboundFlight = head(sortOutboundFlights(byDepartureTime));
      earliestInboundFlight = head(sortInboundFlights(byDepartureTime));
    }
  }

  return {
    outbound: earliestOutboundFlight,
    inbound: earliestInboundFlight,
    tripType,
    isFlightsAvailable,
  };
}

function* dataForRoundTripOrOneWay() {
  const { flights, trekTemplateStartDate, noOfPeople } = yield select(getTrekPaymentState);
  const { start_date: trekStartDate } = yield select(getSelectedTrek);

  const trekDate = trekStartDate ? trekStartDate : trekTemplateStartDate;

  const userData = {
    num_adults: noOfPeople,
  };

  const hardCodedData = {
    nationality: 'US',
    num_children: 0,
  };

  const addDaysToStartDate = days =>
    moment(trekDate)
      .add(Number(days) - 1, 'days')
      .format('YYYY-MM-DD');

  const isOneWayFlight = flights.length === 1;
  if (isOneWayFlight) {
    const isFlightOnFirstDay = flights[0].day === 1;
    return {
      trip_type: 'O',
      ...userData,
      ...hardCodedData,
      departure_date: addDaysToStartDate(isFlightOnFirstDay ? 0 : flights[0].day),
      from_location: config.ENDPOINT.includes('staging') ? 'PKR' : 'LUA',
      to_location: 'KTM',
    };
  } else {
    return {
      trip_type: 'R',
      departure_date: trekDate,
      arrival_date: addDaysToStartDate(flights[1].day),
      ...userData,
      ...hardCodedData,
      from_location: 'KTM',
      to_location: config.ENDPOINT.includes('staging') ? 'PKR' : 'LUA',
    };
  }
}



// WEBPACK FOOTER //
// ./src/redux/sagas/selfGuidedTrek.js